import * as React from 'react'
import { ReactElement, useContext, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Panel, Persona, PersonaSize, PrimaryButton } from '@fluentui/react'
import { Store } from '../reducers'
import { useHistory } from 'react-router-dom'
import { isAppContext } from '../helpers/appHelper'
import { AppContext } from '../config/appConfig'
import useUser from '../hooks/useUser'
import useUsers from '../hooks/useUsers'
import { ModuleType } from '../context/GlobalContext'
import useModules from '../hooks/module/useModules'
import useRoles, { Role, rolesTasks, rolesTimes } from '../hooks/useRoles'
import useSharePoint from '../hooks/useSharePoint'
import { Button, makeStyles, mergeClasses, Switch, tokens } from '@fluentui/react-components'
import { useSettings } from '../hooks/useSettings'
import { useTranslation } from 'react-i18next'
import {
    AlbumFilled,
    AlbumRegular,
    ArrowTrendingLinesFilled,
    ArrowTrendingLinesRegular,
    bundleIcon,
    ClipboardClockFilled,
    ClipboardClockRegular,
    ClipboardTaskFilled,
    ClipboardTaskRegular,
    DocumentCatchUpFilled,
    DocumentCatchUpRegular,
    FlowchartFilled,
    FlowchartRegular,
    GiftCardFilled,
    GiftCardRegular,
    HomeFilled,
    HomeRegular,
    MailFilled,
    MailRegular,
    PaymentFilled,
    PaymentRegular,
    PeopleFilled,
    PeopleRegular,
    PersonAccountsFilled,
    PersonAccountsRegular,
    TextColumnOneWideFilled,
    TextColumnOneWideRegular,
    TimerFilled,
    TimerRegular,
    VehicleCarProfileLtrClockFilled,
    VehicleCarProfileLtrClockRegular,
} from '@fluentui/react-icons'
import { SharePointIcon } from './Icon/SharePointIcon'
import { Timers } from './Timer/Timers'
import useViewport, { Viewport } from '../hooks/useViewport'
import OverflowTabListPanel, { HammockButtonProps, HammockTabData, HammockTabListProps, HammockTabProps } from './Overflow/OverflowTabListPanel'
import { SelectTabData, SelectTabEvent } from '@fluentui/react-tabs'
import useNewVersionPreference, { currentVersion } from '../hooks/useNewVersionPreference'
import { isOfficeInitialized } from '../helpers/outlookHelper'
import AuthorizationContext from '../context/AuthorizationContext'
import { appInsightsReact } from '../pages/App'

type TabItem = HammockTabData & { route?: string; filter?: () => boolean; performClick?: () => void }

export enum MenuItemKey {
    Dashboard = 'dashboard',
    CRM = 'crm',
    Files = 'files',
    Parties = 'parties',
    Emails = 'emails',
    Documents = 'documents',
    Tasks = 'tasks',
    Workflow = 'workflows',
    Times = 'times',
    InvoiceApproval = 'invoice-approval',
    Invoice = 'invoice',
    Reports = 'reports',
    SharePoint = 'sharepoint',
}

export const AlbumIcon = bundleIcon(AlbumFilled, AlbumRegular)
export const ArrowTrendingLinesIcon = bundleIcon(ArrowTrendingLinesFilled, ArrowTrendingLinesRegular)
export const ClipboardTaskIcon = bundleIcon(ClipboardTaskFilled, ClipboardTaskRegular)
export const DocumentBriefcaseIcon = bundleIcon(DocumentCatchUpFilled, DocumentCatchUpRegular)
export const DisbursementIcon = bundleIcon(PaymentFilled, PaymentRegular)
export const FlowchartIcon = bundleIcon(FlowchartFilled, FlowchartRegular)
export const FixedPriceIcon = bundleIcon(GiftCardFilled, GiftCardRegular)
export const HomeIcon = bundleIcon(HomeFilled, HomeRegular)
export const MailIcon = bundleIcon(MailFilled, MailRegular)
export const PeopleIcon = bundleIcon(PeopleFilled, PeopleRegular)
export const TimerIcon = bundleIcon(TimerFilled, TimerRegular)
export const ClipboardClockIcon = bundleIcon(ClipboardClockFilled, ClipboardClockRegular)
export const CRMIcon = bundleIcon(PersonAccountsFilled, PersonAccountsRegular)
export const TextColumnOneWideIcon = bundleIcon(TextColumnOneWideFilled, TextColumnOneWideRegular)
export const TravelIcon = bundleIcon(VehicleCarProfileLtrClockFilled, VehicleCarProfileLtrClockRegular)

const useStyles = makeStyles({
    container: {
        backgroundColor: tokens.colorBrandBackground2,
        display: 'flex',
        justifyContent: 'space-between',
        height: '100%',
    },
    container2: {
        width: '100%',
        display: 'flex',
        flexDirection: 'row-reverse',
    },
    leftContainer: {
        flexGrow: 1,
    },
    farContainer: {
        width: '84px',
    },
    farContainerSmall: {
        width: '60px',
    },
    button: {
        border: 'unset',
        padding: tokens.spacingVerticalS,
        maxWidth: '45px',
        minWidth: '40px',
    },
    buttonXS: {
        border: 'unset',
        padding: tokens.spacingVerticalXS,
        maxWidth: '36px',
        minWidth: '28px',
    },
})

function MainMenuFarItems(props: { buttonProps: HammockButtonProps; containerClassName: string }): ReactElement {
    const modules = useModules()
    const roles = useRoles()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const { currentUser } = useUser()

    const { setHost, reset } = useContext(AuthorizationContext)

    const [isHamburgerOpen, toggleHamburger] = useState<boolean>(false)

    return (
        <div className={mergeClasses(styles.container, props.containerClassName)}>
            {modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes) && <Timers buttonProps={props.buttonProps} />}
            <Button {...props.buttonProps} value={'menu'} icon={<TextColumnOneWideIcon />} onClick={() => toggleHamburger(!isHamburgerOpen)} />
            <Panel isOpen={isHamburgerOpen} onDismiss={() => toggleHamburger(false)}>
                <Persona text={currentUser?.fullName} key="current-persona" size={PersonaSize.size24} />
                {!isOfficeInitialized() && (
                    <PrimaryButton
                        text={translate('menu.logout')}
                        iconProps={{ iconName: 'Leave' }}
                        onClick={() => {
                            if (isOfficeInitialized()) {
                                setHost(undefined)
                            } else {
                                reset()
                            }
                        }}
                    />
                )}
            </Panel>
        </div>
    )
}

export default function MainMenu(): ReactElement {
    const history = useHistory()
    const modules = useModules()
    const roles = useRoles()
    const { settings } = useSettings()
    const { sharePoint } = useSharePoint()
    const styles = useStyles()
    const { t: translate } = useTranslation()
    const users = useUsers()
    const { viewport, isViewport } = useViewport()

    const { loaded: loadedNewVersion, newVersion, setNewVersion } = useNewVersionPreference()

    const breadcrumbs = useSelector<Store, string[]>(state => state.app.breadcrumbs)
    const smallMenu = useMemo<boolean>(() => isViewport('<', Viewport.MainMenu), [viewport])

    const buttonProps = useMemo<HammockButtonProps>(
        () => ({
            appearance: 'subtle',
            className: smallMenu ? styles.buttonXS : styles.button,
            size: smallMenu ? 'small' : 'large',
        }),
        [smallMenu, smallMenu],
    )
    const tabListProps = useMemo<HammockTabListProps>(
        (): HammockTabListProps => ({
            size: smallMenu ? 'small' : 'large',
            appearance: 'subtle',
            selectTabOnFocus: true,
        }),
        [smallMenu],
    )
    const tabProps = useMemo<HammockTabProps>(
        (): HammockTabProps => ({
            className: smallMenu ? styles.buttonXS : styles.button,
        }),
        [smallMenu],
    )

    const onTabSelect = (e: SelectTabEvent, data: SelectTabData) => {
        if (e !== undefined && e.type !== 'click') {
            return
        }

        const tabItem: TabItem = tabs.find(tab => tab.props.value === data.value)
        if (tabItem?.route) {
            history.push(tabItem?.route)
        } else if (tabItem?.performClick && e?.type === 'click') {
            tabItem.performClick()
        }
    }

    const tabs = useMemo<TabItem[]>(
        (): TabItem[] =>
            [
                {
                    titleKey: 'menu.dashboard',
                    route: '/',
                    filter: () => settings?.dashboard?.enabled !== false,
                    props: { value: MenuItemKey.Dashboard, icon: <HomeIcon /> },
                },
                {
                    titleKey: 'menu.crm',
                    route: '/crm',
                    filter: () => modules.has(ModuleType.CRM),
                    props: { value: MenuItemKey.CRM, icon: <CRMIcon /> },
                },
                {
                    titleKey: 'menu.file',
                    route: '/file',
                    props: { value: MenuItemKey.Files, icon: <AlbumIcon /> },
                },
                {
                    titleKey: 'menu.party',
                    route: '/party',
                    props: { value: MenuItemKey.Parties, icon: <PeopleIcon /> },
                },
                {
                    titleKey: 'menu.email',
                    route: '/email',
                    filter: () => isAppContext(AppContext.Read) || isAppContext(AppContext.None),
                    props: { value: MenuItemKey.Emails, icon: <MailIcon /> },
                },
                {
                    titleKey: 'menu.task',
                    route: '/task',
                    filter: () => modules.has(ModuleType.Tasks) && roles.any(rolesTasks),
                    props: { value: MenuItemKey.Tasks, icon: <ClipboardTaskIcon /> },
                },
                {
                    titleKey: 'menu.workflow',
                    route: '/workflow',
                    filter: () => modules.has(ModuleType.Workflow) && roles.any(rolesTasks),
                    props: { value: MenuItemKey.Workflow, icon: <FlowchartIcon /> },
                },
                {
                    titleKey: 'menu.time',
                    route: '/time',
                    filter: () => modules.has(ModuleType.TimeTracking) && roles.any(rolesTimes),
                    props: { value: MenuItemKey.Times, icon: <TimerIcon /> },
                },
                {
                    titleKey: 'menu.invoice.approval',
                    route: '/invoice-approval',
                    filter: () => modules.has(ModuleType.InvoiceApproval) && roles.any(rolesTimes),
                    props: { value: MenuItemKey.InvoiceApproval, icon: <ClipboardClockIcon /> },
                },
                {
                    titleKey: 'menu.invoice',
                    route: '/invoice',
                    filter: () => modules.has(ModuleType.Invoices) && roles.any([Role.Invoice]),
                    props: { value: MenuItemKey.Invoice, icon: <ArrowTrendingLinesIcon /> },
                },
                {
                    titleKey: 'menu.reports',
                    route: '/reports',
                    filter: () => modules.has(ModuleType.Reports) && roles.any([Role.Report]),
                    props: { value: MenuItemKey.Reports, icon: <DocumentBriefcaseIcon /> },
                },
                {
                    titleKey: 'menu.sharepoint',
                    performClick: () => window.open(sharePoint?.list?.webUrl, '_blank'),
                    props: { value: MenuItemKey.SharePoint, icon: <SharePointIcon /> },
                },
            ].filter((item: TabItem) => (item.filter ? item.filter() : true)),
        [roles.loaded, users.loaded, modules.loaded, settings],
    )

    if (!(roles.loaded && users.loaded && modules.loaded && settings)) {
        return <></>
    }

    return (
        <>
            <div className={styles.container}>
                <OverflowTabListPanel
                    tabListProps={tabListProps}
                    tabProps={tabProps}
                    containerClassName={mergeClasses(styles.container, styles.leftContainer)}
                    tabs={tabs}
                    onTabSelect={onTabSelect}
                    selectedValue={breadcrumbs?.[0]}
                    overflowButtonLabelKey={'base.item.label'}
                />
                <MainMenuFarItems buttonProps={buttonProps} containerClassName={smallMenu ? styles.farContainerSmall : styles.farContainer} />
            </div>
            <div className={mergeClasses(styles.container, styles.container2)}>
                {loadedNewVersion && (
                    <Switch
                        labelPosition={'before'}
                        label={translate('menu.newversion')}
                        checked={newVersion}
                        onChange={(_e, data) => {
                            setNewVersion(data.checked)
                            appInsightsReact.trackEvent({
                                name: 'NewHammock',
                                properties: {
                                    reactComponentPath: 'toggle',
                                    version: currentVersion,
                                    value: data.checked,
                                },
                            })
                        }}
                    />
                )}
            </div>
        </>
    )
}
